import { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { convertLbEntryForSave } from "src/generator/hooks/useConvertLbEntry";
import {
  useIsNewEntry,
  useIsOnline,
  usePostApi,
  useUploadFileApi,
} from "src/hooks";
import { type ICredentials } from "src/interfaces";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  reassignNewChecklistActions,
  setActiveAutosave,
  setCurrent,
  setNoSaves,
  setOpenedSaveModal,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
import {
  clearUnsavedRowFlags,
  ESaveEntryReason,
  refetchLogbookInBackground,
  setFiles,
  setLatestEntryId,
  setLatestEntryStatus,
  setSaveReason,
} from "src/redux/slices/generatorSlice";
import { getDateTimeRecord, crumb } from "src/utils";
import { Notification } from "src/utils/Notification";
import { flushStoredChecklistQueue } from "src/generator/hooks/flushChecklistQueue";
import {
  describeFieldsForDiagnostics,
  getActiveFieldNamesForValidation,
  getInvalidActiveFieldNames,
  getOrphanFormFieldKeys,
} from "../utils/collectActiveFormFieldNames";

import { getCurrentUTC } from "../../utils/timeUtils/getCurrentUTC";
import { usePendingFilesContext } from "../LogBar/PendingFilesContext";
import { useAuthorizationUsers } from "./useAuthorizationUsers";
import { useIsPendingWithdraw } from "./useIsPendingWithdraw";
import { useSaveContactNumber } from "./useSaveContactNumber";
import { refreshLogoutTimer } from "../utils/refreshLogoutTimer";
import {
  clearNewSearchParam,
  updateEntryRouteSilently,
} from "../utils/navigateToLogbookEntry";
// import { setWasSaved } from "../../redux/slices/generatorFeaturesSlice";
/**
 * Custom hook to handle saving an entry in the logbook.
 *
 * @returns An object containing methods and properties for saving an entry:
 *   - `saveEntry`: A function to save the entry with provided credentials.
 *   - `loading`: A boolean indicating whether the save operation is in progress.
 *   - `opened`: A boolean indicating whether the save modal is open.
 *   - `open`: A function to open the save modal.
 *   - `close`: A function to close the save modal.
 *   - `usersSelect`: An array of users available for selection.
 *   - `disabled`: A boolean indicating whether the save operation is disabled.
 */
export const useSaveEntry = () => {
  const { post, isLoading } = usePostApi();
  const { upload, isUploading } = useUploadFileApi();
  const { logbookId, idType, lbeidOrPartId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const manualSave = true;
  const { pendingFiles } = usePendingFilesContext() as any;
  const { trigger, formState, getValues, unregister } = useFormContext();
  const isOnline = useIsOnline();
  const isNewEntry = useIsNewEntry();
  const isPendingWithdraw = useIsPendingWithdraw();
  const { yachtid, _prename, entrydate } = useSelector(
    state => state.generatorSlice.entryDataToSave,
  );
  const lbentry = useSelector(state => state.generatorSlice.lbentry);
  const { id, lbName: latestEntryLbName } = useSelector(
    state => state.generatorSlice.latestEntry,
  );
  const logbooks = useSelector(state => state.generatorSlice.logbooks);
  const lbName =
    latestEntryLbName ||
    logbooks.find(logbook => logbook.value === logbookId)?.label ||
    "";
  const { saveReason } = useSelector(state => state.generatorSlice);
  const { current, noSaves, locked, you } = useSelector(
    state => state.entrySlice,
  );
  const sections = lbentry.sections;
  const showContent = useSelector(
    state => state.generatorSlice.features.showContent,
  );
  const opened = useSelector(state => state.entrySlice.openedSaveModal);
  const { saveContactNumber } = useSaveContactNumber();

  const users = useAuthorizationUsers();

  const onSaveSuccess = useCallback(
    async (res: any) => {
      dispatch(setNoSaves(noSaves + 1));
      dispatch(setActiveAutosave(false));
      dispatch(setReleaseEntryLock(Math.random()));
      dispatch(clearUnsavedRowFlags());

      let savedId = res[String(idType)] ?? "";
      let routeIdType = idType;
      if (!savedId) {
        if (res.lbeid) {
          savedId = res.lbeid;
          routeIdType = "lbeid";
        } else if (res.partid) {
          savedId = res.partid;
          routeIdType = "partid";
        } else if (res.meid) {
          savedId = res.meid;
          routeIdType = "meid";
        }
      }
      if (!savedId) return;

      dispatch(setLatestEntryId(savedId));
      if (isNewEntry) {
        dispatch(setLatestEntryStatus({ status: 1, autosave: 0 }));
      }

      dispatch(
        reassignNewChecklistActions({ realId: savedId, lbid: logbookId, yachtid }),
      );
      void flushStoredChecklistQueue(post, savedId, dispatch);

      // Save contact number after successful manual save
      await saveContactNumber();

      if (isNewEntry && pendingFiles.length > 0) {
        const body: Record<string, string> = {};
        body[String(idType)] = savedId;
        await upload("upload-file", pendingFiles, body, async uploadRes => {
          dispatch(setOpenedSaveModal(false));
          dispatch(setFiles(uploadRes.attachments));
          Notification(
            "Success",
            `The files has been added successfully`,
            "green",
          );
        });
      }

      if (String(lbeidOrPartId) !== String(savedId)) {
        updateEntryRouteSilently(
          dispatch,
          navigate,
          setSearchParams,
          String(logbookId),
          String(routeIdType),
          savedId,
        );
      }
      refreshLogoutTimer();
      dispatch(setOpenedSaveModal(false));
      clearNewSearchParam(setSearchParams);
      dispatch(refetchLogbookInBackground());
    },
    [
      dispatch,
      idType,
      isNewEntry,
      lbeidOrPartId,
      logbookId,
      navigate,
      post,
      upload,
      noSaves,
      saveContactNumber,
      setSearchParams,
    ],
  );
  const saveEntry = useCallback(
    async (credentials: ICredentials) => {
      const startTime = Date.now();
      const isOfflineNew = lbeidOrPartId?.includes("new=");
      const isExplicitNew =
        isNewEntry &&
        (searchParams.get("new") === "true" || id === "new" || !lbeidOrPartId);
      const entryId = isExplicitNew
        ? "new"
        : isOfflineNew
          ? lbeidOrPartId
          : (lbeidOrPartId ?? id);

      // Log manual save attempt
      crumb("Manual Save: Save entry initiated", {
        isNewEntry,
        entryId,
        logbookId,
        saveReason,
        userId: credentials.userid,
        hasFiles: pendingFiles.length > 0,
        filesCount: pendingFiles.length,
      });

      try {
        const { payload, removedBlankRows } = convertLbEntryForSave(
          lbentry,
          getValues(),
          manualSave,
          true,
        );
        const saveReasonToSend =
          saveReason !== ESaveEntryReason.none
            ? saveReason
            : removedBlankRows
              ? ESaveEntryReason.removedBlankRows
              : undefined;

        const res = await post({
          request: "save-entry",
          body: {
            params: {
              _current: current,
              entrydate,
              dto: getDateTimeRecord(),
              lbid: logbookId,
              ...credentials,
              yachtid,
              [String(idType)]: entryId,
              _prename,
              ...(saveReasonToSend ? { reason: saveReasonToSend } : {}),
              lbentry: payload,
            },
            _lbid: logbookId,
            _lbname: lbName,
            ...(window?.api ? { _idInRoute: !!lbeidOrPartId } : {}),
          },
          onSuccess: onSaveSuccess,
        });

        if (res) {
          const duration = Date.now() - startTime;

          // Log successful manual save
          crumb("Manual Save: Save entry completed", {
            isNewEntry,
            entryId,
            logbookId,
            duration,
            status: res.status,
            success: true,
            hourlyRowsCount: sections[1]?.rows?.length ?? undefined,
          });

          dispatch(setReleaseEntryLock(Math.random()));
          dispatch(setCurrent(res.current || getCurrentUTC()));
        }

        dispatch(setSaveReason(ESaveEntryReason.none));
      } catch (error) {
        const duration = Date.now() - startTime;

        // Log failed manual save
        crumb("Manual Save: Save entry failed", {
          isNewEntry,
          entryId,
          logbookId,
          duration,
          error: error instanceof Error ? error.message : "Unknown error",
          success: false,
        });

        throw error;
      }
    },
    [
      isNewEntry,
      lbeidOrPartId,
      id,
      post,
      entrydate,
      lbentry,
      getValues,
      logbookId,
      yachtid,
      idType,
      _prename,
      lbName,
      onSaveSuccess,
      current,
      saveReason,
      searchParams,
    ],
  );
  const validateActiveFields = useCallback(async () => {
    // Stale "-exclusive-" registrations (left behind when a row/cell is
    // regenerated with fresh UUIDs but never unregistered) share their
    // exclusive group id with the live field, so the group-wide required
    // check in validateExclusiveRequiredGroup sees the ghost's empty value
    // and fails the whole group even though the current field is filled in.
    // Drop them before validating so only live fields participate.
    const orphanKeys = getOrphanFormFieldKeys(getValues(), sections, {
      excludeBlankRows: lbentry.continousEntry,
      showContent,
    });
    const staleExclusiveKeys = orphanKeys.filter(
      key => key.includes("-exclusive-") || key.includes("-exclusiveUser-"),
    );
    if (staleExclusiveKeys.length > 0) {
      unregister(staleExclusiveKeys);
    }

    const fieldNames = getActiveFieldNamesForValidation(
      getValues(),
      sections,
      {
        excludeBlankRows: lbentry.continousEntry,
        showContent,
      },
    );
    if (fieldNames.length === 0) {
      return { fieldNames, result: true };
    }
    const result = await trigger(fieldNames);
    return { fieldNames, result };
  }, [
    getValues,
    sections,
    lbentry.continousEntry,
    showContent,
    trigger,
    unregister,
  ]);

  const openModal = useCallback(async () => {
    let validation = await validateActiveFields();

    if (!validation.result) {
      await new Promise(resolve => setTimeout(resolve, 100));
      validation = await validateActiveFields();
    }

    if (!validation.result) {
      const values = getValues();
      const orphans = getOrphanFormFieldKeys(getValues(), sections, {
        excludeBlankRows: lbentry.continousEntry,
        showContent,
      });
      const invalidActiveFieldNames = getInvalidActiveFieldNames(
        formState.errors as Record<string, unknown>,
        validation.fieldNames,
      );
      const invalidActiveFields = describeFieldsForDiagnostics(
        invalidActiveFieldNames,
        sections,
      );
      const invalidActiveFieldValues = invalidActiveFieldNames
        .slice(0, 10)
        .map(name => {
          const err = (formState.errors as Record<string, any>)[name];
          return {
            field: name,
            value: (values as Record<string, unknown>)[name],
            error: err
              ? {
                  nums: err.nums && { type: err.nums.type, message: err.nums.message },
                  dir: err.dir && { type: err.dir.type, message: err.dir.message },
                  type: err.type,
                  message: err.message,
                }
              : undefined,
          };
        });
      crumb("Manual Save: Validation failed", {
        orphanFieldCount: orphans.length,
        orphanFields: orphans.slice(0, 10),
        invalidFieldCount: Object.keys(formState.errors).length,
        activeFieldCount: validation.fieldNames.length,
        activeFields: validation.fieldNames.slice(0, 25),
        invalidActiveFieldCount: invalidActiveFieldNames.length,
        invalidActiveFieldNames: invalidActiveFieldNames.slice(0, 25),
        invalidActiveFields: invalidActiveFields.slice(0, 10),
        invalidActiveFieldValues,
        topLevelFormKeyCount: Object.keys(values).length,
      });
      Notification("Error", "You need to fill all required fields", "red");
      return;
    }

    dispatch(setOpenedSaveModal(true));
  }, [
    dispatch,
    formState.errors,
    getValues,
    lbentry.continousEntry,
    showContent,
    sections,
    validateActiveFields,
  ]);

  const closeModal = useCallback(() => {
    dispatch(setOpenedSaveModal(false));
  }, [dispatch]);

  const disabled = useMemo(
    () => isPendingWithdraw || (isOnline && locked && !you),
    [isOnline, isPendingWithdraw, locked, you],
  );

  return {
    saveEntry,
    loading: isLoading || isUploading,
    opened,
    open: openModal,
    close: closeModal,
    usersSelect: users,
    disabled,
  };
};
